"use client";

import TextSubHeader from "@/components/typography/TextSubHeader";
import TextHeader from "@/components/typography/TextHeader";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import getLeadFormAgreementsData from "@/hooks/data/getLeadFormAgreementsData";
import getLeadSchema from "@/hooks/formSchemas/getLeadSchema";
import { LeadFormFieldsType } from "@/types/LeadFormFieldsType";
import DrawForm from "@/screens/homepage/components/drawings/DrawForm";
import LeadForm from "@/components/sections/LeadForm";
import { LeadFlowType } from "@/types/LeadFlowType";
import IconDownload from "@/components/icons/IconDownload";
import Image from "next/image";
import { ApartamentType } from "@/types/ApartamentType";
import getApartmentsData from "@/hooks/data/getApartmentsData";
import TextContent from "@/components/typography/TextContent";
import IconRoomPlan from "@/components/icons/IconRoomPlan";
import IconPriceTag from "@/components/icons/IconPriceTag";
import React, { useRef, useState } from "react";
import Button from "@/components/ui/Button";
import { sendMail } from "@/services/send-email";
import { renderToString } from "react-dom/server";
import striptags from "striptags";
import { useSearchParams } from "next/navigation";
import ReCAPTCHA from "react-google-recaptcha";
import IconFloor from "@/components/icons/IconFloor";

export default function Form({ flow }: { flow?: null | LeadFlowType }) {
  const [isMailSent, setIsMailSent] = useState<boolean>(false);
  const agreements = getLeadFormAgreementsData();
  const apartment: ApartamentType =
    getApartmentsData().filter(
      (apartment) => apartment.id === flow?.apartmentId,
    )[0] ?? null;

  const form = useForm<LeadFormFieldsType>({
    resolver: zodResolver(getLeadSchema(agreements)),
  });

  const searchParams = useSearchParams();

  const reCaptcha = useRef<ReCAPTCHA>(null);

  const onSubmit = async (values: LeadFormFieldsType): Promise<void> => {
    if (1 || (reCaptcha && reCaptcha.current)) {
      // const captcha = await reCaptcha.current.executeAsync();7
      const captcha = true;
      if (captcha) {
        const acceptedAgreements: Array<string> = [];
        getLeadFormAgreementsData().forEach((agreement) => {
          if (
            typeof values["agreement" + agreement.id] !== "undefined" &&
            values["agreement" + agreement.id] === true
          ) {
            acceptedAgreements.push(
              agreement.id +
                " (" +
                striptags(renderToString(agreement.content)) +
                ")",
            );
          }
        });

        const mail = [
          ["INWESTYCJA", "Tremonti Szczyrk"],
          ["IMIĘ", values.firstName],
          ["EMAIL", values.email],
          ["TELEFON", values.phone],
          ["OPIS", values.message],
          ["ZGODY", acceptedAgreements.join("\n\n")],
        ];

        const searchParamsWithLead: {
          [key: string]: string;
        } = {
          utm_campaign: "Campaign Name",
          utm_source: "Campaign Source",
          utm_medium: "Campaign Medium",
          utm_content: "Campaign Content",
          utm_term: "Campaign Term",
          gclid: "Google Click ID",
          gaclientid: "Google Analytics Client ID",
          fbclid: "Facebook Click ID",
        };
        searchParams.forEach((value, name) => {
          if (searchParamsWithLead.hasOwnProperty(name) && value) {
            mail.push([searchParamsWithLead[name], value]);
          }
        });

        const mailText = mail.map((item) => item.join(":\n")).join("\n\n");

        const response = await sendMail({
          sendTo: "lead@wprost.voxdeveloper.pl",
          sendToCC: "lead@condo.pl",
          email: values.email,
          subject: "Formularz kontaktowy",
          text: mailText,
        });
        if (response?.messageId) {
          setIsMailSent(true);
        } else {
        }
      }
    }
  };

  let typAfterContent = <></>;
  let preContent = <></>;
  let cta = <>Zapytaj o ofertę</>;
  let title = (
    <h2>
      <span className="relative z-10">Kup apartament</span>
      <br />
      <span className="relative inline-block">
        <span className="relative z-10">i zarabiaj!</span>
        <DrawForm className="absolute -inset-x-2 top-2" />
      </span>
    </h2>
  );
  let subtitle = (
    <>
      Zostaw nam swoje dane,
      <br /> zadzwonimy do Ciebie!
    </>
  );
  if (flow) {
    if (flow.flow === "apartment" || flow.flow === "offer") {
      preContent = (
        <div className={"mb-7 grid grid-cols-2 gap-5 border-b-2 pb-7"}>
          <div>
            <TextSubHeader>{apartment.name}</TextSubHeader>
            <div className={"space-y-2"}>
              <div className="flex items-center">
                <IconFloor className={"mr-3 w-8"} />
                <TextContent className="!font-bold">
                  {apartment.floor}
                </TextContent>
              </div>
              <div className="flex items-center">
                <IconRoomPlan className={"mr-3 w-8"} />
                <TextContent className="!font-bold">
                  {apartment.inside.reduce(
                    (sum, inside) => sum + inside.metric,
                    0,
                  )}{" "}
                  m<sup>2</sup>
                </TextContent>
              </div>
              <div className="flex items-center">
                <IconPriceTag className={"mr-3 w-8"} />
                <TextContent className="!font-bold">
                  {apartment.price} zł
                </TextContent>
              </div>
            </div>
          </div>
          <div className={"flex items-center"}>
            <Image src={apartment.imageDraw} alt={""} />
          </div>
        </div>
      );
    }
    if (flow.flow === "apartment") {
      cta = <>Zapytaj o ten apartament</>;
      title = (
        <>
          <span className="relative inline-block">
            <span className="relative z-10">Zapytaj</span>
            <DrawForm className="absolute -left-2 top-2" />
          </span>{" "}
          <span className="relative z-10">o ten apartament</span>
        </>
      );
    } else if (flow.flow === "club") {
      title = <>Kup apartament i dołącz do klubu inwestorów</>;
      subtitle = <>Korzystaj z rabatów w hotelach sieci Tremonti®!</>;
      cta = <>Zapytaj</>;
    } else if (flow.flow === "offer") {
      typAfterContent = (
        <a title={"Przejdź do oferty"} href={"/apartament/" + apartment.slug}>
          <Button className={"!w-full"}>Przejdź do oferty</Button>
        </a>
      );
      cta = (
        <>
          <span>Pobierz ofertę</span>
          <IconDownload className={"ml-2.5 w-8"} />
        </>
      );
      title = (
        <>
          <span className="relative inline-block">
            <span className="relative z-10">Pobierz ofertę</span>
            <DrawForm className="absolute -left-2 top-1 h-full" />
          </span>{" "}
          <span className="relative z-10">tego apartamentu</span>
        </>
      );
    }
  }

  if (isMailSent) {
    return (
      <div className="space-y-6 lg:space-y-7">
        <div>
          <TextHeader>Dziękujemy za wypełnienie formularza!</TextHeader>
          <TextSubHeader>
            Odezwiemy się do Ciebie najszybciej jak to możliwe.
          </TextSubHeader>
        </div>
        {typAfterContent}
      </div>
    );
  }

  return (
    <div>
      {preContent}
      <div className="mb-6 lg:mb-7">
        <TextHeader>{title}</TextHeader>
        <TextSubHeader>{subtitle}</TextSubHeader>
      </div>
      <LeadForm
        form={form}
        agreements={agreements}
        onSubmit={onSubmit}
        cta={cta}
        reCaptcha={reCaptcha}
      />
    </div>
  );
}
